import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
    Timeline,
    Text,
    TextInput,
    ActionIcon,
    Box,
    Flex,
    Title,
    Loader,
    Button,
    Stack,
    Modal,
    Group
} from '@mantine/core';
import {
    IconSend,
    IconAlertCircle,
    IconCheck,
    IconEdit,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { MyUserDataContext } from '../contexts';
import { ProductComment, CustomClaims } from '../types';
import {
    DocumentReference,
    Timestamp,
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    FirestoreError,
    doc,
    updateDoc,
} from 'firebase/firestore';

interface ProductCommentProps {
    collectionRef: DocumentReference;
}

const TIMER_INTERVAL = 60000; // 1 minute
const MIN_COMMENT_LENGTH = 3;
const MAX_COMMENT_LENGTH = 1000;
const VISIBLE_COMMENTS_LIMIT = 3;

const TIMELINE_COLORS = [
    '#2c7be5', // Trustworthy Blue
    '#00a86b', // Jade Green 
    '#00bcd4', // Deep Cyan 
    '#f4b400', // Golden Yellow  
    '#e67e22', // Warm Orange 
    '#8e44ad', // Amethyst Purple 
    '#7f8c8d', // Slate Gray 
    '#5dade2', // Sky Blue 
    '#27ae60', // Emerald Green  
    '#f39c12', // Sunflower Yellow  
    '#af7ac5', // Lavender Purple 
    '#48c9b0'  // Soft Teal
];

// Function to generate consistent color from string
const stringToColor = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % TIMELINE_COLORS.length;
    return TIMELINE_COLORS[index];
};

// Function to generate initials from name
const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CustomProductComment: React.FC<ProductCommentProps> = ({ collectionRef }) => {
    const [comments, setComments] = useState<ProductComment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [showAllComments, setShowAllComments] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Edit/Delete states
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const userContext = useContext(MyUserDataContext);
    const userClaims = useMemo(() =>
        userContext?.firebaseTokenResult?.claims?.customClaims as CustomClaims || {},
        [userContext?.firebaseTokenResult?.claims?.customClaims]
    );
    const user = userContext?.user;

    // Real-time timer for updating relative timestamps
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, TIMER_INTERVAL);

        return () => clearInterval(timer);
    }, []);

    // Firestore real-time listener
    useEffect(() => {
        if (!collectionRef || !user) {
            setIsFetching(false);
            return;
        }

        setIsFetching(true);

        try {
            const commentsRef = collection(collectionRef, 'comments');
            const queryDb = query(commentsRef, orderBy('createdAt', 'asc'));

            const unsubscribe = onSnapshot(
                queryDb,
                (snapshot) => {
                    const commentsData = snapshot.docs.map((doc) => ({
                        id: doc.id,
                        ...doc.data()
                    })) as ProductComment[];

                    // Filter out soft-deleted comments
                    const activeComments = commentsData.filter(comment => !comment.deleted);
                    setComments(activeComments);
                    setIsFetching(false);
                },
                (firestoreError: FirestoreError) => {
                    console.error('Firestore listener error:', firestoreError);
                    setIsFetching(false);

                    notifications.show({
                        title: 'Connection Error',
                        message: 'Unable to sync comments. Please check your connection.',
                        color: 'red',
                        icon: <IconAlertCircle size={16} />
                    });
                }
            );

            return () => unsubscribe();
        } catch (err) {
            console.error('Setup error:', err);
            setIsFetching(false);
        }
    }, [collectionRef, user]);

    const validateComment = useCallback((comment: string): string | null => {
        const trimmed = comment.trim();

        if (!trimmed) {
            return 'Comment cannot be empty';
        }
        if (trimmed.length < MIN_COMMENT_LENGTH) {
            return `Comment must be at least ${MIN_COMMENT_LENGTH} characters long`;
        }
        if (trimmed.length > MAX_COMMENT_LENGTH) {
            return `Comment must be less than ${MAX_COMMENT_LENGTH} characters`;
        }

        return null;
    }, []);

    const formatDate = useCallback((timestamp: any) => {
        if (!timestamp) return { day: '', month: '', fullTime: '' };

        const date = timestamp instanceof Date ? timestamp : timestamp.toDate?.() || new Date(timestamp);
        const now = currentTime;
        const diffMs = now.getTime() - date.getTime();

        // Date boundary calculations
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        const startOfCommentDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const timeString = date.toLocaleString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        let fullTime: string;

        if (diffMs < 60000) {
            fullTime = 'now';
        } else if (diffMs < 3600000) {
            const minutes = Math.floor(diffMs / 60000);
            fullTime = `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
        } else if (diffMs < 7200000) {
            fullTime = '1 hour ago';
        } else if (startOfCommentDay.getTime() === startOfToday.getTime()) {
            fullTime = `Today ${timeString}`;
        } else if (startOfCommentDay.getTime() === startOfYesterday.getTime()) {
            fullTime = `Yesterday ${timeString}`;
        } else {
            fullTime = date.toLocaleString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        }

        return {
            day: date.getDate().toString(),
            month: date.toLocaleString('default', { month: 'short' }),
            fullTime
        };
    }, [currentTime]);

    const addComment = useCallback(async () => {
        if (!user) {
            notifications.show({
                title: 'Authentication Required',
                message: 'Please log in to add a comment',
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
            return;
        }

        const validationError = validateComment(newComment);
        if (validationError) {
            notifications.show({
                title: 'Validation Error',
                message: validationError,
                color: 'orange',
                icon: <IconAlertCircle size={16} />
            });
            return;
        }

        setIsLoading(true);

        const comment: Omit<ProductComment, 'id'> = {
            comment: newComment.trim(),
            createdBy: user.displayName || user.email || 'Unknown User',
            createdById: user.uid,
            createdByEmail: user.email || '',
            customClaims: userClaims,
            createdAt: Timestamp.now(),
            deleted: false
        };

        try {
            const commentsRef = collection(collectionRef, 'comments');
            await addDoc(commentsRef, comment);

            setNewComment('');

            notifications.show({
                title: 'Success',
                message: 'Comment added successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });
        } catch (err) {
            console.error('Error adding comment:', err);
            notifications.show({
                title: 'Error',
                message: `Failed to add comment: ${err instanceof Error ? err.message : 'Unknown error'}`,
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setIsLoading(false);
        }
    }, [newComment, user, userClaims, collectionRef, validateComment]);

    const startEdit = useCallback((comment: ProductComment) => {
        setEditingCommentId(comment.id!);
        setEditText(comment.comment);
    }, []);

    const cancelEdit = useCallback(() => {
        setEditingCommentId(null);
        setEditText('');
    }, []);

    const saveEdit = useCallback(async (commentId: string) => {
        const validationError = validateComment(editText);
        if (validationError) {
            notifications.show({
                title: 'Validation Error',
                message: validationError,
                color: 'orange',
                icon: <IconAlertCircle size={16} />
            });
            return;
        }

        setActionLoading(commentId);

        try {
            const commentsRef = collection(collectionRef, 'comments');
            const commentDoc = doc(commentsRef, commentId);

            await updateDoc(commentDoc, {
                comment: editText.trim(),
                updatedAt: Timestamp.now(),
                updatedBy: user?.uid
            });

            setEditingCommentId(null);
            setEditText('');

            notifications.show({
                title: 'Success',
                message: 'Comment updated successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });
        } catch (err) {
            console.error('Error updating comment:', err);
            notifications.show({
                title: 'Error',
                message: 'Failed to update comment',
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setActionLoading(null);
        }
    }, [editText, validateComment, collectionRef, user?.uid]);

    const confirmDelete = useCallback((commentId: string) => {
        setCommentToDelete(commentId);
        setDeleteModalOpen(true);
    }, []);

    const softDeleteComment = useCallback(async () => {
        if (!commentToDelete) return;

        setActionLoading(commentToDelete);

        try {
            const commentsRef = collection(collectionRef, 'comments');
            const commentDoc = doc(commentsRef, commentToDelete);

            await updateDoc(commentDoc, {
                deleted: true,
                deletedAt: Timestamp.now(),
                deletedBy: user?.uid
            });

            setDeleteModalOpen(false);
            setCommentToDelete(null);

            notifications.show({
                title: 'Success',
                message: 'Comment deleted successfully',
                color: 'green',
                icon: <IconCheck size={16} />
            });
        } catch (err) {
            console.error('Error deleting comment:', err);
            notifications.show({
                title: 'Error',
                message: 'Failed to delete comment',
                color: 'red',
                icon: <IconAlertCircle size={16} />
            });
        } finally {
            setActionLoading(null);
        }
    }, [commentToDelete, collectionRef, user?.uid]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            addComment();
        }
    }, [addComment]);

    const handleEditKeyPress = useCallback((e: React.KeyboardEvent, commentId: string) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            saveEdit(commentId);
        } else if (e.key === 'Escape') {
            cancelEdit();
        }
    }, [saveEdit, cancelEdit]);

    const toggleShowAllComments = useCallback(() => {
        setShowAllComments(prev => !prev);
    }, []);

    // Memoized timeline items
    const timelineItems = useMemo(() =>
        comments.map((comment, index) => {
            const dateInfo = formatDate(comment.createdAt);
            const isCurrentUser = comment.createdById === user?.uid;
            const isEditing = editingCommentId === comment.id;

            return {
                bullet: (
                    <Box
                        style={{
                            backgroundColor: stringToColor(comment.createdBy),
                            color: 'white',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            flexDirection: 'column'
                        }}
                    >
                        <Text size="xs" style={{ lineHeight: 1, fontSize: '10px', fontWeight: 800 }}>
                            {getInitials(comment.createdBy)}
                        </Text>
                    </Box>
                ),
                title: (
                    <Box>
                        {isEditing ? (
                            <Flex gap="sm" align="flex-start" mb="xs">
                                <TextInput
                                    style={{ flex: 1 }}
                                    value={editText}
                                    onChange={(e) => setEditText(e.currentTarget.value)}
                                    onKeyDown={(e) => handleEditKeyPress(e, comment.id!)}
                                    size="sm"
                                    error={editText.length > MAX_COMMENT_LENGTH ? 'Comment too long' : null}
                                    autoFocus
                                />
                                <Group gap="xs">
                                    <ActionIcon
                                        size="sm"
                                        color="green"
                                        onClick={() => saveEdit(comment.id!)}
                                        loading={actionLoading === comment.id}
                                        disabled={editText.trim().length < MIN_COMMENT_LENGTH || editText.length > MAX_COMMENT_LENGTH}
                                    >
                                        <IconCheck size={14} />
                                    </ActionIcon>
                                    <ActionIcon
                                        size="sm"
                                        color="gray"
                                        onClick={cancelEdit}
                                        disabled={actionLoading === comment.id}
                                    >
                                        <IconX size={14} />
                                    </ActionIcon>
                                </Group>
                            </Flex>
                        ) : (
                            <Flex justify="space-between" align="flex-start">
                                <Box style={{ flex: 1 }}>
                                    <Text fw={600} size="md" c="dark">
                                        {comment.comment}
                                        {comment.updatedAt && (
                                            <Text component="span" size="xs" c="dimmed" ml="xs">
                                                (edited) {formatDate(comment.updatedAt).fullTime}
                                            </Text>
                                        )}
                                    </Text>
                                </Box>
                                {isCurrentUser && (
                                    <Group gap="xs">
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="blue"
                                            onClick={() => startEdit(comment)}
                                            title="Edit comment"
                                        >
                                            <IconEdit size={14} />
                                        </ActionIcon>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="red"
                                            onClick={() => confirmDelete(comment.id!)}
                                            title="Delete comment"
                                        >
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </Group>
                                )}
                            </Flex>
                        )}
                        <Text size="sm" c="dimmed">
                            {comment.createdBy} {isCurrentUser ? '(You)' : ''}
                        </Text>
                    </Box>
                ),
                children: (
                    <Box>
                        <Text size="sm" c="dark">
                            {dateInfo.fullTime}
                        </Text>
                    </Box>
                )
            };
        }),
        [comments, formatDate, user?.uid, editingCommentId, editText, actionLoading, startEdit, confirmDelete, saveEdit, cancelEdit, handleEditKeyPress]
    );

    // Comments display logic
    const { commentsToShow, timelineItemsToShow } = useMemo(() => {
        const toShow = showAllComments ? comments : comments.slice(-VISIBLE_COMMENTS_LIMIT);
        const itemsToShow = showAllComments ? timelineItems : timelineItems.slice(-VISIBLE_COMMENTS_LIMIT);

        return {
            commentsToShow: toShow,
            timelineItemsToShow: itemsToShow
        };
    }, [showAllComments, comments, timelineItems]);

    const isCommentValid = newComment.trim().length >= MIN_COMMENT_LENGTH &&
        newComment.length <= MAX_COMMENT_LENGTH;

    return (
        <>
            <Stack gap={0}>
                {/* Header */}
                <Box mb="lg">
                    <Flex justify="space-between" align="center">
                        <Title order={3}>Comments</Title>
                        {comments.length > VISIBLE_COMMENTS_LIMIT && (
                            <Button
                                variant="subtle"
                                size="xs"
                                onClick={toggleShowAllComments}
                            >
                                {showAllComments ? 'Show less' : `Show all ${comments.length} comments`}
                            </Button>
                        )}
                    </Flex>
                </Box>

                {/* Timeline */}
                <Box style={{ flex: 1 }}>
                    {isFetching ? (
                        <Box mt="xl">
                            <Loader size="md" />
                            <Text size="sm" c="dimmed" mt="sm">Loading comments...</Text>
                        </Box>
                    ) : comments.length === 0 ? (
                        <Box mt="xl">
                            <Text c="dimmed">No comments yet</Text>
                            <Text size="sm" c="dimmed">Be the first to add a comment</Text>
                        </Box>
                    ) : (
                        <Timeline
                            active={commentsToShow.length}
                            bulletSize={24}
                            lineWidth={2}
                            styles={{
                                itemBullet: {
                                    border: 'none',
                                    backgroundColor: 'transparent !important'
                                }
                            }}
                        >
                            {timelineItemsToShow.map((item, index) => (
                                <Timeline.Item
                                    key={commentsToShow[index]?.id || `comment-${index}`}
                                    bullet={item.bullet}
                                    color={stringToColor(commentsToShow[index]?.createdBy)}
                                    title={item.title}
                                >
                                    {item.children}
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    )}
                </Box>

                {/* Input Box */}
                <Box mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
                    <Flex gap="sm" align="flex-end">
                        <TextInput
                            style={{ flex: 1 }}
                            placeholder={`Type your comment... (min ${MIN_COMMENT_LENGTH} chars)`}
                            value={newComment}
                            onChange={(e) => setNewComment(e.currentTarget.value)}
                            onKeyDown={handleKeyPress}
                            disabled={isLoading || !user}
                            size="sm"
                            error={newComment.length > MAX_COMMENT_LENGTH ? 'Comment too long' : null}
                        />
                        <ActionIcon
                            onClick={addComment}
                            disabled={!isCommentValid || isLoading || !user}
                            color={isCommentValid && !isLoading && user ? 'blue' : 'gray'}
                            variant={isCommentValid && !isLoading && user ? 'filled' : 'light'}
                            size="lg"
                            loading={isLoading}
                        >
                            <IconSend size={16} />
                        </ActionIcon>
                    </Flex>
                    <Text size="xs" c="dimmed" mt="xs">
                        {newComment.length}/{MAX_COMMENT_LENGTH} characters
                    </Text>
                </Box>
            </Stack>

            {/* Delete Confirmation Modal */}
            <Modal
                opened={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Delete Comment"
                size="sm"
            >
                <Text size="sm" mb="lg">
                    Are you sure you want to delete this comment? This action cannot be undone.
                </Text>
                <Group justify="flex-end" gap="sm">
                    <Button
                        variant="light"
                        color="gray"
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={actionLoading === commentToDelete}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="red"
                        onClick={softDeleteComment}
                        loading={actionLoading === commentToDelete}
                    >
                        Delete Comment
                    </Button>
                </Group>
            </Modal>
        </>
    );
};

export default CustomProductComment;